"use client";

import * as React from "react";
import {
  format,
  addMonths,
  subMonths,
  getDaysInMonth,
  startOfMonth,
  getDay,
  isSameDay,
  isBefore,
  startOfToday,
} from "date-fns";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateType } from "@/types/coupon/discount";
import { ScrollArea } from "./scroll-area";
import { toast } from "sonner";

const months = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: format(new Date(0, i), "MMMM"),
}));

const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() + i,
);

export function DateTimePicker({
  date,
  onDateChange,
  disabledDate,
  startDate,
  isEndPicker = false,
}: {
  date: DateType | null;
  onDateChange: (date: DateType | null) => void;
  disabledDate?: (d: Date) => boolean;
  startDate?: DateType | null;
  isEndPicker?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [displayDate, setDisplayDate] = React.useState(date || new Date());

  const handleDateSelect = (day: number) => {
    const dayDate = new Date(
      displayDate.getFullYear(),
      displayDate.getMonth(),
      day,
    );
    const today = startOfToday();
    const isPast = isBefore(dayDate, today);
    const isDisabledByProp = disabledDate ? disabledDate(dayDate) : false;

    if (isPast || isDisabledByProp) {
      toast.error("Selecting past dates is not allowed.");
      return;
    }

    const newDate = date ? new Date(date) : new Date();
    newDate.setFullYear(displayDate.getFullYear(), displayDate.getMonth(), day);
    onDateChange(newDate);
  };

  const handleTimeChange = (
    type: "hour" | "minute" | "ampm",
    value: string,
  ) => {
    const newDate = date ? new Date(date) : new Date();
    if (type === "hour") {
      newDate.setHours(
        (parseInt(value) % 12) + (newDate.getHours() >= 12 ? 12 : 0),
      );
    } else if (type === "minute") {
      newDate.setMinutes(parseInt(value));
    } else if (type === "ampm") {
      const currentHours = newDate.getHours();
      if (value === "PM" && currentHours < 12) {
        newDate.setHours(currentHours + 12);
      } else if (value === "AM" && currentHours >= 12) {
        newDate.setHours(currentHours - 12);
      }
    }
    onDateChange(newDate);
  };

  const daysInMonth = getDaysInMonth(displayDate);
  const monthStart = startOfMonth(displayDate);
  const startDayOfWeek = getDay(monthStart);
  const today = startOfToday();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal !bg-background !border-border hover:text-muted-foreground",
            !date && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            format(date, "MM/dd/yyyy hh:mm aa")
          ) : (
            <span>Select date</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 flex">
        <div className="p-3">
          <div className="flex justify-between items-center mb-2">
            <div className="flex gap-2">
              <Select
                value={displayDate.getMonth().toString()}
                onValueChange={(m) =>
                  setDisplayDate(
                    new Date(displayDate.getFullYear(), parseInt(m)),
                  )
                }
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={displayDate.getFullYear().toString()}
                onValueChange={(y) =>
                  setDisplayDate(new Date(parseInt(y), displayDate.getMonth()))
                }
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => setDisplayDate(subMonths(displayDate, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => setDisplayDate(addMonths(displayDate, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-sm">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="font-medium text-muted-foreground">
                {day}
              </div>
            ))}
            {Array.from({ length: startDayOfWeek }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const dayDate = new Date(
                displayDate.getFullYear(),
                displayDate.getMonth(),
                day,
              );
              const isPast = isBefore(dayDate, today);
              const isDisabledByProp = disabledDate
                ? disabledDate(dayDate)
                : false;
              const isDisabled = isPast || isDisabledByProp;
              const isSelected = date ? isSameDay(dayDate, date) : false;
              return (
                <Button
                  key={day}
                  variant={isSelected ? "default" : "ghost"}
                  size="icon"
                  className={cn(
                    "h-8 w-8 rounded-full",
                    isDisabled &&
                      "bg-slate-200 text-muted-foreground opacity-50 cursor-not-allowed dark:bg-slate-800",
                  )}
                  onClick={() => handleDateSelect(day)}
                >
                  {day}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="flex border-l">
          <ScrollArea className="h-80 w-20">
            <div className="p-2">
              <div className="mb-2 text-center text-sm font-medium">Hour</div>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => {
                let isDisabled = false;
                if (
                  isEndPicker &&
                  date &&
                  startDate &&
                  date.getFullYear() === startDate.getFullYear() &&
                  date.getMonth() === startDate.getMonth() &&
                  date.getDate() === startDate.getDate()
                ) {
                  const startHour = startDate.getHours() % 12 || 12;
                  if (hour < startHour) isDisabled = true;
                }
                return (
                  <Button
                    key={hour}
                    variant={
                      date && date.getHours() % 12 === hour % 12
                        ? "default"
                        : "ghost"
                    }
                    className={cn(
                      "w-full justify-start",
                      isDisabled &&
                        "bg-slate-200 text-muted-foreground opacity-50 cursor-not-allowed dark:bg-slate-800",
                    )}
                    disabled={isDisabled}
                    onClick={() => handleTimeChange("hour", hour.toString())}
                  >
                    {hour.toString().padStart(2, "0")}
                  </Button>
                );
              })}
            </div>
          </ScrollArea>
          <ScrollArea className="h-80 w-20 border-l">
            <div className="p-2">
              <div className="mb-2 text-center text-sm font-medium">Minute</div>
              {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => {
                let isDisabled = false;
                if (
                  isEndPicker &&
                  date &&
                  startDate &&
                  date.getFullYear() === startDate.getFullYear() &&
                  date.getMonth() === startDate.getMonth() &&
                  date.getDate() === startDate.getDate() &&
                  date.getHours() === startDate.getHours() &&
                  minute < startDate.getMinutes()
                ) {
                  isDisabled = true;
                }
                return (
                  <Button
                    key={minute}
                    variant={
                      date && date.getMinutes() === minute ? "default" : "ghost"
                    }
                    className={cn(
                      "w-full justify-start",
                      isDisabled &&
                        "bg-slate-200 text-muted-foreground opacity-50 cursor-not-allowed dark:bg-slate-800",
                    )}
                    disabled={isDisabled}
                    onClick={() =>
                      handleTimeChange("minute", minute.toString())
                    }
                  >
                    {minute.toString().padStart(2, "0")}
                  </Button>
                );
              })}
            </div>
          </ScrollArea>
          <ScrollArea className="h-80 w-20 border-l">
            <div className="p-2">
              <div className="mb-2 text-center text-sm font-medium">Period</div>
              {["AM", "PM"].map((ampm) => {
                let isDisabled = false;
                if (
                  isEndPicker &&
                  date &&
                  startDate &&
                  date.getFullYear() === startDate.getFullYear() &&
                  date.getMonth() === startDate.getMonth() &&
                  date.getDate() === startDate.getDate()
                ) {
                  const startIsPM = startDate.getHours() >= 12;
                  if (ampm === "AM" && startIsPM) isDisabled = true;
                }
                return (
                  <Button
                    key={ampm}
                    variant={
                      date &&
                      ((ampm === "AM" && date.getHours() < 12) ||
                        (ampm === "PM" && date.getHours() >= 12))
                        ? "default"
                        : "ghost"
                    }
                    className={cn(
                      "w-full justify-start",
                      isDisabled &&
                        "bg-slate-200 text-muted-foreground opacity-50 cursor-not-allowed dark:bg-slate-800",
                    )}
                    disabled={isDisabled}
                    onClick={() => handleTimeChange("ampm", ampm)}
                  >
                    {ampm}
                  </Button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
